<!-- ================================================================
  GOST Matrix — Cliniva Table Card Pattern
  Structure: header → body (scroll table)
================================================================ -->

<!-- Card Header -->
<div class="gost-matrix__details-header">
  <app-details-header
    [title]="headerTitle"
    [badgeText]="headerBadgeText"
    [patientName]="headerPatientName()"
    [measureDate]="headerMeasureDate()"
    [measurer]="headerMeasurer()"
    [useCustomPdfHandler]="true"
    (pdfClicked)="onPrint()" />
</div>

<div class="matrix-header">
  <div class="matrix-header__left">
    <span class="matrix-header__label" i18n="@@home.gostMatrix.label.range">表示範囲</span>
    <button
      class="matrix-header__select"
      [matMenuTriggerFor]="windowMenu"
      aria-label="表示範囲を選択"
      i18n-aria-label="@@home.gostMatrix.ariaLabel.selectRange">
      <span class="matrix-header__select-text" i18n="@@home.gostMatrix.option.previousCount">前{{ windowSize() }}回</span>
      <mat-icon class="matrix-header__select-arrow">arrow_drop_down</mat-icon>
    </button>
    <mat-menu #windowMenu="matMenu" class="matrix-window-menu">
      @for (size of windowSizeOptions; track size) {
        <button
          mat-menu-item
          (click)="onWindowSizeChange(size)"
          [class.matrix-menu-item--active]="windowSize() === size">
          <span i18n="@@home.gostMatrix.option.previousCount">前{{ size }}回</span>
        </button>
      }
    </mat-menu>
  </div>

  <div class="matrix-header__right">
    <button
      mat-icon-button
      class="matrix-header__action-btn"
      (click)="onPrint()"
      matTooltip="PDF出力"
      i18n-matTooltip="@@home.gostMatrix.tooltip.pdf"
      aria-label="PDF出力"
      i18n-aria-label="@@home.gostMatrix.ariaLabel.pdf">
      <mat-icon>picture_as_pdf</mat-icon>
    </button>
    <a
      mat-icon-button
      class="matrix-header__action-btn"
      routerLink="../manual"
      matTooltip="手入力する"
      i18n-matTooltip="@@home.gostMatrix.tooltip.manual"
      aria-label="手入力する"
      i18n-aria-label="@@home.gostMatrix.ariaLabel.manual">
      <mat-icon>edit_note</mat-icon>
    </a>
    <button
      mat-icon-button
      class="matrix-header__action-btn"
      (click)="onRefresh()"
      [disabled]="isLoading()"
      matTooltip="更新"
      i18n-matTooltip="@@home.gostMatrix.tooltip.refresh"
      aria-label="更新"
      i18n-aria-label="@@home.gostMatrix.ariaLabel.refresh">
      <mat-icon [class.spinning]="isLoading()">refresh</mat-icon>
    </button>
  </div>
</div>

<!-- Loading -->
@if (isLoading()) {
  <div class="matrix-body" role="status" aria-label="Loading GOST data">
    <table class="matrix-table" aria-hidden="true">
      <thead>
        <tr>
          <th class="matrix-table__label-col" i18n="@@home.gostMatrix.column.item">項目</th>
          @for (_ of skeletonColumns; track $index) {
            <th>
              <div class="skel-bone skel-bone--badge"></div>
            </th>
          }
        </tr>
      </thead>
      <tbody>
        @for (row of skeletonRows; track $index) {
          <tr [class]="row.rowClass ?? ''">
            <th class="matrix-table__label-col" scope="row">{{ row.label }}</th>
            @for (_ of skeletonColumns; track $index) {
              <td>
                @if (row.type === 'pills') {
                  <div class="skel-pills">
                    <div class="skel-bone skel-bone--pill"></div>
                    <div class="skel-bone skel-bone--pill"></div>
                  </div>
                } @else if (row.type === 'date') {
                  <div class="skel-bone skel-bone--date"></div>
                } @else if (row.type === 'num') {
                  <div class="skel-bone skel-bone--num"></div>
                } @else {
                  <div class="skel-bone skel-bone--text"></div>
                }
              </td>
            }
          </tr>
        }
      </tbody>
    </table>
    <span class="visually-hidden">Loading...</span>
  </div>
} @else if (visibleColumns().length === 0) {
  <!-- Empty -->
  <mat-card class="matrix-empty">
    <mat-card-content i18n="@@home.gostMatrix.empty">
      GOSTレコードがありません。手入力または計測後に経過表を表示できます。
    </mat-card-content>
  </mat-card>
} @else {
  <!-- Table Body -->
  <div class="matrix-body">
    <table class="matrix-table" role="table" aria-label="GOST経過表">
      <thead>
        <tr>
          <th class="matrix-table__label-col" i18n="@@home.gostMatrix.column.item">項目</th>
          @for (column of visibleColumns(); track column.recordId; let i = $index) {
            <th
              [class.col--hovered]="hoveredColIndex() === i"
              (mouseenter)="hoveredColIndex.set(i)"
              (mouseleave)="hoveredColIndex.set(null)">
              <a
                class="matrix-table__col-badge"
                (click)="openRecord(column.recordId)"
                matTooltip="詳細を表示"
                i18n-matTooltip="@@home.gostMatrix.tooltip.openRecord">
                {{ getColumnLabel(column.offset) }}
                <mat-icon class="matrix-table__col-badge-icon">open_in_new</mat-icon>
              </a>
            </th>
          }
        </tr>
      </thead>

      <tbody>
        @for (row of detailRows(); track row.key) {
          <tr [class]="row.rowClass ?? ''">
            <th class="matrix-table__label-col" scope="row">{{ row.label }}</th>
            @for (cell of row.cells; track $index; let i = $index) {
              <td
                [class]="'cell--' + cell.tone"
                [class.col--hovered]="hoveredColIndex() === i"
                (mouseenter)="hoveredColIndex.set(i)"
                (mouseleave)="hoveredColIndex.set(null)">
                @if ((cell.pills?.length ?? 0) > 0) {
                  <div class="matrix-pills">
                    @for (pill of cell.pills ?? []; track pill.key) {
                      <span
                        class="matrix-pill"
                        [class.matrix-pill--bad]="pill.tone === 'bad'"
                        [class.matrix-pill--acceptable]="pill.tone === 'acceptable'">
                        {{ pill.text }}
                      </span>
                    }
                  </div>
                } @else {
                  {{ cell.text }}
                }
              </td>
            }
          </tr>
        }
      </tbody>
    </table>
  </div>
}
